/**
 * Phone helpers for the lead forms (FLOSEAI-415).
 *
 * The lead phone field must always carry a country code. We store and validate
 * numbers in E.164 form (`+<countryCode><nationalNumber>`). To avoid pulling in
 * a heavyweight phone library, this module ships a self-contained country table
 * and derives flag emoji from each ISO code, so the data stays small and the
 * behaviour is deterministic across iOS, Android, and web.
 */

export interface Country {
  /** ISO 3166-1 alpha-2 code, uppercase. */
  iso: string;
  /** English display name. */
  name: string;
  /** Calling code digits, without the leading `+`. */
  dial: string;
}

/**
 * The country every lookup falls back to when an ISO code is unknown. Declared
 * as its own value and spliced into `COUNTRIES` below, so the fallback is a
 * real member of the table by construction rather than by convention.
 */
export const DEFAULT_COUNTRY: Country = { iso: 'CO', name: 'Colombia', dial: '57' };

/**
 * Curated list of calling codes. Ordered so that, when two countries share a
 * dial code (e.g. the NANP `+1`), the first entry wins during parsing.
 */
export const COUNTRIES: readonly Country[] = [
  DEFAULT_COUNTRY,
  { iso: 'US', name: 'United States', dial: '1' },
  { iso: 'CA', name: 'Canada', dial: '1' },
  { iso: 'MX', name: 'Mexico', dial: '52' },
  { iso: 'AR', name: 'Argentina', dial: '54' },
  { iso: 'BR', name: 'Brazil', dial: '55' },
  { iso: 'CL', name: 'Chile', dial: '56' },
  { iso: 'PE', name: 'Peru', dial: '51' },
  { iso: 'VE', name: 'Venezuela', dial: '58' },
  { iso: 'EC', name: 'Ecuador', dial: '593' },
  { iso: 'BO', name: 'Bolivia', dial: '591' },
  { iso: 'PY', name: 'Paraguay', dial: '595' },
  { iso: 'UY', name: 'Uruguay', dial: '598' },
  { iso: 'PA', name: 'Panama', dial: '507' },
  { iso: 'CR', name: 'Costa Rica', dial: '506' },
  { iso: 'GT', name: 'Guatemala', dial: '502' },
  { iso: 'SV', name: 'El Salvador', dial: '503' },
  { iso: 'HN', name: 'Honduras', dial: '504' },
  { iso: 'NI', name: 'Nicaragua', dial: '505' },
  { iso: 'DO', name: 'Dominican Republic', dial: '1809' },
  { iso: 'PR', name: 'Puerto Rico', dial: '1787' },
  { iso: 'CU', name: 'Cuba', dial: '53' },
  { iso: 'ES', name: 'Spain', dial: '34' },
  { iso: 'PT', name: 'Portugal', dial: '351' },
  { iso: 'GB', name: 'United Kingdom', dial: '44' },
  { iso: 'IE', name: 'Ireland', dial: '353' },
  { iso: 'FR', name: 'France', dial: '33' },
  { iso: 'DE', name: 'Germany', dial: '49' },
  { iso: 'IT', name: 'Italy', dial: '39' },
  { iso: 'NL', name: 'Netherlands', dial: '31' },
  { iso: 'BE', name: 'Belgium', dial: '32' },
  { iso: 'CH', name: 'Switzerland', dial: '41' },
  { iso: 'AT', name: 'Austria', dial: '43' },
  { iso: 'SE', name: 'Sweden', dial: '46' },
  { iso: 'NO', name: 'Norway', dial: '47' },
  { iso: 'DK', name: 'Denmark', dial: '45' },
  { iso: 'FI', name: 'Finland', dial: '358' },
  { iso: 'PL', name: 'Poland', dial: '48' },
  { iso: 'CZ', name: 'Czechia', dial: '420' },
  { iso: 'RO', name: 'Romania', dial: '40' },
  { iso: 'GR', name: 'Greece', dial: '30' },
  { iso: 'TR', name: 'Turkey', dial: '90' },
  { iso: 'RU', name: 'Russia', dial: '7' },
  { iso: 'UA', name: 'Ukraine', dial: '380' },
  { iso: 'IL', name: 'Israel', dial: '972' },
  { iso: 'AE', name: 'United Arab Emirates', dial: '971' },
  { iso: 'SA', name: 'Saudi Arabia', dial: '966' },
  { iso: 'ZA', name: 'South Africa', dial: '27' },
  { iso: 'NG', name: 'Nigeria', dial: '234' },
  { iso: 'EG', name: 'Egypt', dial: '20' },
  { iso: 'KE', name: 'Kenya', dial: '254' },
  { iso: 'MA', name: 'Morocco', dial: '212' },
  { iso: 'IN', name: 'India', dial: '91' },
  { iso: 'PK', name: 'Pakistan', dial: '92' },
  { iso: 'BD', name: 'Bangladesh', dial: '880' },
  { iso: 'CN', name: 'China', dial: '86' },
  { iso: 'JP', name: 'Japan', dial: '81' },
  { iso: 'KR', name: 'South Korea', dial: '82' },
  { iso: 'ID', name: 'Indonesia', dial: '62' },
  { iso: 'PH', name: 'Philippines', dial: '63' },
  { iso: 'TH', name: 'Thailand', dial: '66' },
  { iso: 'VN', name: 'Vietnam', dial: '84' },
  { iso: 'MY', name: 'Malaysia', dial: '60' },
  { iso: 'SG', name: 'Singapore', dial: '65' },
  { iso: 'AU', name: 'Australia', dial: '61' },
  { iso: 'NZ', name: 'New Zealand', dial: '64' },
];

export const DEFAULT_COUNTRY_ISO = DEFAULT_COUNTRY.iso;

/** E.164 shape: leading `+`, then 2–15 digits (country code + national number). */
export const E164_REGEX = /^\+[1-9]\d{1,14}$/;

/**
 * Minimum length of the national number, counted WITHOUT the country code.
 * `+57` + 8 digits is not enough — the local number itself must be ≥10 digits.
 */
export const MIN_NATIONAL_DIGITS = 10;

const COUNTRY_BY_ISO = new Map(COUNTRIES.map((c) => [c.iso, c]));

/** Strip everything that is not a digit. */
function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

/** Convert an ISO alpha-2 code into its flag emoji (regional indicators). */
export function flagEmoji(iso: string): string {
  const code = iso.trim().toUpperCase();
  if (code.length !== 2 || !/^[A-Z]{2}$/.test(code)) return '🏳️';
  return String.fromCodePoint(...[...code].map((char) => 0x1f1e6 + char.charCodeAt(0) - 65));
}

export function getCountry(iso: string): Country {
  return COUNTRY_BY_ISO.get(iso) ?? DEFAULT_COUNTRY;
}

/** Build an E.164 string from a country dial code and a national number. */
export function toE164(dial: string, nationalNumber: string): string {
  const national = onlyDigits(nationalNumber);
  if (!national) return '';
  return `+${onlyDigits(dial)}${national}`;
}

/**
 * Split a stored phone value into its country + national parts. Handles E.164
 * input (`+57...`) as well as legacy values without a `+`, falling back to the
 * default country when no calling code can be recognised.
 */
export function parsePhone(value: string | null | undefined): {
  iso: string;
  national: string;
} {
  const raw = (value ?? '').trim();
  const digits = onlyDigits(raw);
  if (!digits) return { iso: DEFAULT_COUNTRY_ISO, national: '' };

  // Only auto-detect the country when an explicit `+` is present. A bare digit
  // string can coincidentally start with a foreign dial code, so guessing would
  // silently mangle legacy/local numbers — keep the default country instead.
  if (raw.startsWith('+')) {
    const match = COUNTRIES.filter((c) => digits.startsWith(c.dial)).sort(
      (a, b) => b.dial.length - a.dial.length,
    )[0];
    if (match) {
      return { iso: match.iso, national: digits.slice(match.dial.length) };
    }
  }

  return { iso: DEFAULT_COUNTRY_ISO, national: digits };
}

/**
 * A lead phone is valid only when it is E.164 with a country code AND its
 * national number (the digits after the country code) is at least
 * `MIN_NATIONAL_DIGITS` long. The country code is never counted toward that
 * minimum, so `+57` + 8 digits is rejected even though it has 10 digits total.
 */
export function isValidLeadPhone(value: string | null | undefined): boolean {
  const raw = (value ?? '').trim();
  if (!E164_REGEX.test(raw)) return false;
  return parsePhone(raw).national.length >= MIN_NATIONAL_DIGITS;
}
